import { createContext, type ComponentChildren } from 'preact';
import * as stylex from '@stylexjs/stylex';
import { Tooltip } from '../Tooltip/tooltip';
import { styles } from './field.styles';

/** The enclosing `Field`'s label, for a control that wants to name itself after
 *  it. `SearchSelect` uses it to render "Select {label}" instead of
 *  every call site repeating the label as a placeholder. */
export const FieldLabelContext = createContext<string | undefined>(undefined);
interface FieldProps {
  label?: string | undefined;
  hint?: string | undefined;
  /** Validation message — shown in danger below the control (replaces the hint
   *  while present) and announced politely. Pair with the control's `invalid`. */
  error?: string | undefined;
  /** Mandatory-field affordance (S-1): a danger asterisk after the label — the
   *  same convention the schema-driven attribute forms already use. Visual
   *  only; wire actual enforcement on the control/submit path. */
  required?: boolean | undefined;
  /** What this field MEANS, behind a "?" beside the label.
   *
   *  Distinct from `hint`, and the split is the point: a hint is always visible
   *  and belongs to filling the field in ("Format: HH:MM"); help is the
   *  definition an operator needs ONCE and should not have to read on every
   *  render. Putting definitions in `hint` is what turns a form into a wall of
   *  grey text nobody reads, including the sentence that mattered. */
  help?: string | undefined;
  /** Open the help bubble leftwards — set it on the last field in a row so it
   *  does not overflow the panel. */
  helpAlignEnd?: boolean | undefined;
  htmlFor?: string | undefined;
  children: ComponentChildren;
}
export function Field({
  label,
  hint,
  error,
  required = false,
  help,
  helpAlignEnd = false,
  htmlFor,
  children
}: FieldProps) {
  return <FieldLabelContext.Provider value={label}>
      <div {...stylex.props(styles.field)}>
        {label != null && <span {...stylex.props(styles.labelRow)}>
            <label htmlFor={htmlFor} {...stylex.props(styles.label)}>
              {label}
              {required && <span aria-hidden="true" {...stylex.props(styles.required)}>
                  {' *'}
                </span>}
            </label>
            {/* Outside the <label>: a button inside a label steals the click that
              should focus the control. */}
            {help != null && <Tooltip text={help} label={label} alignEnd={helpAlignEnd} />}
          </span>}
        {children}
        {error != null ? <span aria-live="polite" {...stylex.props(styles.meta, styles.error)}>
            {error}
          </span> : hint != null && <span {...stylex.props(styles.meta, styles.hint)}>{hint}</span>}
      </div>
    </FieldLabelContext.Provider>;
}